import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { type SplitWithOffset, hasSplitOffset } from '@racemap/utilities/functions/map';
import { RacemapGeoTypes, type SplitObject } from '@racemap/utilities/types/geos';
import type { AcceptedReadRust } from '@racemap/utilities/types/trackping';
import type { Reader } from '@racemap/utilities/types/types';
import { type FC, memo } from 'react';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import { getFeaturesOfType } from '../../lib/geoUtils/getFeaturesOfType';
import { useStore } from '../../store/reducers';
import { ElevationChart, type ElevationChartMarker, SplitsChart } from '../Charts';

interface ExtendedMarker extends ElevationChartMarker {
  refToReader?: Reader | null;
  refToAcceptedPing?: AcceptedReadRust | null;
}

interface Props {
  readers?: Array<Reader>;
  acceptedReads?: Array<AcceptedReadRust>;
  readerInFocus?: Reader | null;
  withSplitChart: boolean;
  onHoverPosition?: (index: number | null) => void;
  onReaderInFocusChange?: (reader: Reader | null) => void;
  acceptedReadInFocus?: AcceptedReadRust | null;
  onAcceptedReadInFocusChange?: (acceptedPing: AcceptedReadRust | null) => void;
}

export const ItemsOverOffsetChart: FC<Props> = memo(function ItemsOverOffsetChart({
  readers,
  acceptedReads,
  readerInFocus,
  withSplitChart,
  onHoverPosition,
  onReaderInFocusChange,
  acceptedReadInFocus,
  onAcceptedReadInFocusChange,
}: Props) {
  const event = useCurrentEvent();
  const shadowtrack = useStore((state) => state.prediction.chunkedShadowTrack);

  if (event == null || shadowtrack == null) return <></>;

  const unitType = event.playerOptions.unitType;
  const splits = getFeaturesOfType<SplitObject>(event.geo?.features, RacemapGeoTypes.SPLIT);
  const splitsWithOffset = splits.filter(hasSplitOffset);

  const markers: Array<ExtendedMarker> = [
    ...splitsWithOffset.map((s: SplitWithOffset) => ({
      name: 'Split',
      coordinateIndex: Math.floor(s.properties.offset / 10),
      color: RacemapColors.DarkGray,
      offset: s.properties.offset,
    })),
  ];

  if (Array.isArray(readers)) {
    markers.push(
      ...readers.flatMap((reader) =>
        reader.mappings.map((mapping, mi) => ({
          name: `${reader.name || reader.readerId || ''}${
            reader.mappings.length > 1 ? `.${mi + 1}` : ''
          }`,
          coordinateIndex: Math.floor(mapping.offset / 10),
          color: RacemapColors.DarkGreen,
          highlight: reader.readerId === readerInFocus?.readerId,
          offset: mapping.offset,
          refToReader: reader,
        })),
      ),
    );
  }

  if (Array.isArray(acceptedReads)) {
    markers.push(
      ...acceptedReads.flatMap((acceptedPing) => {
        return {
          name: `${acceptedPing.readerId || ''}`,
          coordinateIndex: Math.floor(acceptedPing.offset / 10),
          color: RacemapColors.DarkBlue,
          highlight: acceptedPing.readerId === acceptedReadInFocus?.readerId,
          offset: acceptedPing.offset,
          refToAcceptedPing: acceptedPing,
        };
      }),
    );
  }

  const handleMarkerHover = (marker: ExtendedMarker | null) => {
    if (marker == null) {
      onReaderInFocusChange?.(null);
      onAcceptedReadInFocusChange?.(null);
    } else {
      if (marker.refToReader != null) {
        onReaderInFocusChange?.(marker.refToReader);
      }
      if (marker.refToAcceptedPing != null) {
        onAcceptedReadInFocusChange?.(marker.refToAcceptedPing);
      }
    }
  };

  return (
    <Container>
      <ElevationChart
        track={shadowtrack}
        markers={markers}
        onHoverPosition={onHoverPosition}
        onHoverMarker={handleMarkerHover}
        unitType={unitType}
      />
      {withSplitChart && (
        <SplitsChart
          splits={splits}
          markers={markers}
          useForLabel="defaultSpeed"
          unitType={unitType}
          defaultSpeed={event.defaultSpeed}
        />
      )}
    </Container>
  );
});

const Container = styled.div`
  grid-area: map;
  position: relative;
  bottom: 0;
  left: 0;
  right: 0;
  height: 220px;
  min-width: 0;
  max-width: calc(100% - 50px);
  box-sizing: border-box;
  align-self: end;  
  margin: 25px;
  padding-bottom: 6px;
  border: solid 0.5px #36739a;
  border-radius: 5px;
  background: #00000040;
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
`;
